import type { ReactNode } from "react";
import { holatVar } from "./palette";

export interface HBarSegment {
	value: number;
	color: string;
	label: string;
}

export interface HBarRow {
	key: string;
	label: ReactNode;
	value: number;
	/** ⚠️ Formatlangan MATN — qiymat raqam sifatida ham ko'rinadi. */
	valueText: string;
	color?: string;
	/** Berilsa bar shu bo'laklarga bo'linadi (bo'lak kengligi — bar ichidagi ulush). */
	segments?: HBarSegment[];
	/** Berilsa qator bosiladigan bo'ladi (reja imzosidan kengaytirilgan). */
	onClick?: () => void;
}

export interface HBarListProps {
	rows: HBarRow[];
	/** Berilmasa — qatorlardagi eng katta qiymat. */
	max?: number;
	/** Vertikal maqsad chizig'i, `max` shkalasida. */
	target?: number;
	/** Bo'sh ro'yxat matni (tarjima qilingan). */
	empty: string;
}

function foiz(qiymat: number, max: number): number {
	if (!(max > 0 && qiymat > 0)) {
		return 0;
	}

	return Math.round(Math.min(100, (qiymat / max) * 100) * 100) / 100;
}

function matnYorliq(label: ReactNode): string | undefined {
	return typeof label === "string" || typeof label === "number" ? String(label) : undefined;
}

/**
 * Kutubxonasiz gorizontal bar ro'yxati (sof CSS).
 *
 * ⚠️ **Qiymat o'ng chetda MATN sifatida ham turadi** — rang va uzunlik
 * yagona kanal bo'lmasin.
 *
 * ⚠️ **`data-bar-name` / `data-bar` / `data-bar-target` — TEST
 * KONTRAKTI** (dashboard `DepartmentAttendanceCard.test.tsx` shu
 * atributlardan o'qiydi, `BarList` endi shu komponentning moslamasi).
 */
export function HBarList({ rows, max, target, empty }: HBarListProps) {
	if (rows.length === 0) {
		return (
			<div className="py-4 text-center text-xs" style={{ color: "var(--chart-axis)" }}>
				{empty}
			</div>
		);
	}

	const shkala = max ?? Math.max(0, ...rows.map((qator) => qator.value));

	return (
		<div data-bar-list className="flex flex-col gap-2">
			{rows.map((qator) => (
				<BarQatori key={qator.key} qator={qator} shkala={shkala} target={target} />
			))}
		</div>
	);
}

function BarQatori({
	qator,
	shkala,
	target,
}: {
	qator: HBarRow;
	shkala: number;
	target: number | undefined;
}) {
	const nom = matnYorliq(qator.label);
	const bosish = qator.onClick;

	return (
		// biome-ignore lint/a11y/noStaticElementInteractions: `role`/`tabIndex`/klaviatura pastda SHARTLI — `onClick` bo'lmasa qator oddiy matn
		<div
			role={bosish ? "button" : undefined}
			tabIndex={bosish ? 0 : undefined}
			className="flex items-center gap-3"
			onClick={bosish}
			onKeyDown={
				bosish
					? (e) => {
							if (e.key === "Enter" || e.key === " ") {
								bosish();
							}
						}
					: undefined
			}
			style={bosish ? { cursor: "pointer" } : undefined}
		>
			<div data-bar-name className="w-36 shrink-0 truncate text-xs" title={nom}>
				{qator.label}
			</div>

			<div
				className="relative h-2.5 flex-1 overflow-hidden rounded-full"
				style={{ backgroundColor: "var(--chart-grid)" }}
			>
				<div
					data-bar
					title={nom === undefined ? qator.valueText : `${nom}: ${qator.valueText}`}
					className="flex h-full overflow-hidden rounded-full"
					style={{
						width: `${foiz(qator.value, shkala)}%`,
						backgroundColor: qator.segments ? undefined : (qator.color ?? holatVar("kelgan")),
					}}
				>
					{qator.segments ? <Bolaklar segments={qator.segments} /> : null}
				</div>

				{target === undefined ? null : (
					<div
						data-bar-target
						className="absolute top-0 h-full"
						style={{
							left: `${foiz(target, shkala)}%`,
							width: 2,
							backgroundColor: holatVar("neytral"),
						}}
					/>
				)}
			</div>

			{/* ⚠️ `w-14` EMAS: uzun matn («207 · ruxsatsiz 205») bir qatorda qolsin, bar qisqaradi. */}
			<div className="min-w-14 shrink-0 whitespace-nowrap text-right text-xs tabular-nums">
				{qator.valueText}
			</div>
		</div>
	);
}

/** Bo'lak kengligi — bar ICHIDAGI ulush (bar kengligi `value/max`). */
function Bolaklar({ segments }: { segments: HBarSegment[] }) {
	const jami = segments.reduce((a, s) => a + Math.max(0, s.value), 0);

	return segments.map((bolak) => (
		<i
			key={bolak.label}
			data-bar-segment
			title={`${bolak.label}: ${bolak.value}`}
			className="block h-full"
			style={{ width: `${foiz(bolak.value, jami)}%`, backgroundColor: bolak.color }}
		/>
	));
}
